// GET /dashboard/esg/{persona}/{org}/by-branch[/export] — flat payloads.
// Branch waste comes from the same pickup orders as the dashboard (collected = the
// branch's completed kg), so the branch rows add up to the dashboard's totals.
// Utilities and training come from the shared monthly ESG series, split by site share.

import {
  DEMO_FACILITIES,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  DEMO_SITES,
  materialById,
  monthlyEsgSeries,
  round,
  siteShare,
  type DemoEsgMonth,
  type DemoPickupOrder,
} from '../core';
import type {
  CarbonAvoidedByStream,
  EsgBranchMonthlyRow,
  EsgBranchWasteExportResponse,
  EsgDashboardByBranchResponse,
  EsgDashboardPersona,
  EsgUtilityScalars,
} from '../../types/esg-dashboard.type';
import { fillReportsForSite, monthBounds } from './containers';
import { dashboardStatistics, materialBreakdown } from './dashboard';
import { categoryName, lastMonthKeys, materialName, pct, recoveryShare, shiftMonth, sum } from './helpers';
import { orderKg, ordersInMonth } from './pickupOrders';

/** Commercial tariffs (SAR): water per m3, electricity per kWh, diesel per litre, gas per m3. */
const TARIFF = { water: 7.5, electricity: 0.2, diesel: 1.66, gas: 1.25 };
const FACTOR_VERSION = 'DEFRA 2025 v1.1';

interface Branch {
  id: string;
  name: string;
}

function branchesFor(orgId: string): Branch[] {
  if (orgId === DEMO_OUTLET_ORG_ID) return DEMO_SITES.map((s) => ({ id: s.id, name: s.name_en }));
  if (orgId === DEMO_RECYCLER_ORG_ID) return DEMO_FACILITIES.map((f) => ({ id: f.id, name: f.name_en }));
  return [];
}

function branchOf(orgId: string, order: DemoPickupOrder): string | null {
  return orgId === DEMO_RECYCLER_ORG_ID ? order.facility_id : order.site_id;
}

function esgMonths(orgId: string): Map<string, DemoEsgMonth> {
  return new Map(monthlyEsgSeries(orgId, 12).map((m) => [m.month, m]));
}

function utilities(months: DemoEsgMonth[], share: number): EsgUtilityScalars {
  const water = sum(months, (m) => m.water_m3) * share;
  const kwh = sum(months, (m) => m.electricity_kwh) * share;
  const diesel = sum(months, (m) => m.diesel_l) * share;
  const gas = sum(months, (m) => m.gas_m3) * share;
  return {
    water_consumption: round(water, 1),
    water_amount_sar: round(water * TARIFF.water, 2),
    electricity_consumption_kwh: round(kwh, 0),
    electricity_amount_sar: round(kwh * TARIFF.electricity, 2),
    oil_consumption_liters: round(diesel, 1),
    oil_amount_sar: round(diesel * TARIFF.diesel, 2),
    gas_consumption_m3: round(gas, 1),
    gas_amount_sar: round(gas * TARIFF.gas, 2),
  };
}

function priorScalars(u: EsgUtilityScalars, fillReports: number) {
  return {
    previous_month_water_consumption: u.water_consumption,
    previous_month_water_amount_sar: u.water_amount_sar,
    previous_month_electricity_consumption_kwh: u.electricity_consumption_kwh,
    previous_month_electricity_amount_sar: u.electricity_amount_sar,
    previous_month_oil_consumption_liters: u.oil_consumption_liters,
    previous_month_oil_amount_sar: u.oil_amount_sar,
    previous_month_gas_consumption_m3: u.gas_consumption_m3,
    previous_month_gas_amount_sar: u.gas_amount_sar,
    previous_month_fill_level_report_count: fillReports,
  };
}

function recycledOrders(orders: readonly DemoPickupOrder[]): Array<{ order: DemoPickupOrder; recycledKg: number }> {
  return orders
    .filter((o) => o.status === 'completed')
    .map((order) => ({ order, recycledKg: orderKg(order) * recoveryShare(order.material_id) }));
}

function carbon(orders: readonly DemoPickupOrder[]) {
  const byMaterial = new Map<string, number>();
  for (const { order, recycledKg } of recycledOrders(orders)) {
    const factor = materialById(order.material_id)?.emission_factor_tco2e_per_t ?? 0;
    const label = materialName(order.material_id);
    byMaterial.set(label, (byMaterial.get(label) ?? 0) + (recycledKg / 1000) * factor);
  }
  const streams: CarbonAvoidedByStream[] = [...byMaterial.entries()]
    .map(([material_label, t]) => ({ material_label, total_avoided_tco2e: round(t, 3), emission_factor_versions: [FACTOR_VERSION] }))
    .sort((a, b) => b.total_avoided_tco2e - a.total_avoided_tco2e);
  const done = orders.filter((o) => o.status === 'completed');
  return {
    total_avoided_tco2e: round(sum(streams, (s) => s.total_avoided_tco2e), 3),
    avoided_tco2e_by_stream: streams,
    calculation_run_count: done.filter((o) => o.approval_status !== 'rejected').length,
    calculation_skipped_count: done.filter((o) => o.approval_status === 'rejected').length,
  };
}

/** Recycled kg by category (with material names) — what left the line as recyclate. */
function recycledBreakdown(orders: readonly DemoPickupOrder[]) {
  return materialBreakdown(orders).map((row) => {
    const materials = row.materials.map((m) => {
      const sample = orders.find((o) => materialName(o.material_id) === m.material_type);
      const share = sample ? recoveryShare(sample.material_id) : 0.9;
      return { material_type: m.material_type, amount: round(m.amount * share, 1) };
    });
    return { waste_type: row.waste_type, total_kg: round(sum(materials, (m) => m.amount), 1), materials };
  });
}

function reportWindow(key: string | null): { from: number; to: number } {
  if (key) return monthBounds(key);
  const keys = lastMonthKeys(12);
  return { from: monthBounds(keys[0]).from, to: Date.now() };
}

function branchRow(
  orgId: string,
  branch: Branch,
  key: string | null,
  months: Map<string, DemoEsgMonth>,
): EsgBranchMonthlyRow {
  const keys = key ? [key] : lastMonthKeys(12);
  const share = siteShare(branch.id);
  const all = ordersInMonth(orgId, key).filter((o) => branchOf(orgId, o) === branch.id);
  const requested = sum(all, orderKg);
  const collected = sum(all.filter((o) => o.status === 'completed'), orderKg);
  const recycled = sum(recycledOrders(all), (r) => r.recycledKg);
  const window = reportWindow(key);
  const fillReports = orgId === DEMO_OUTLET_ORG_ID ? fillReportsForSite(branch.id, window.from, window.to) : 0;
  const prevKey = shiftMonth(key ?? keys[keys.length - 1], -1);
  const prevWindow = monthBounds(prevKey);
  const prevFill = orgId === DEMO_OUTLET_ORG_ID ? fillReportsForSite(branch.id, prevWindow.from, prevWindow.to) : 0;
  const current = utilities(keys.map((k) => months.get(k)).filter((m): m is DemoEsgMonth => Boolean(m)), share);
  const prev = utilities([months.get(prevKey)].filter((m): m is DemoEsgMonth => Boolean(m)), share);
  return {
    collection_site_id: branch.id,
    collection_site_name: branch.name,
    total_waste_diverted_kg: round(collected, 1),
    waste_diversion_rate_percent: pct(collected, requested),
    total_waste_recycled_kg: round(recycled, 1),
    recycled_waste_breakdown_by_type: recycledBreakdown(all),
    fill_level_report_count: fillReports,
    ...current,
    ...priorScalars(prev, prevFill),
    ...carbon(all),
  };
}

/** By-branch payload for a month ('YYYY-MM') or the whole history (null). */
export function esgByBranch(persona: EsgDashboardPersona, orgId: string, key: string | null): EsgDashboardByBranchResponse {
  const months = esgMonths(orgId);
  const branches = branchesFor(orgId).map((b) => branchRow(orgId, b, key, months));
  const keys = key ? [key] : lastMonthKeys(12);
  const prevKey = shiftMonth(key ?? keys[keys.length - 1], -1);
  const prevRows = branches.map((b) => ({
    u: utilities([months.get(prevKey)].filter((m): m is DemoEsgMonth => Boolean(m)), siteShare(b.collection_site_id)),
    fill: b.previous_month_fill_level_report_count,
  }));
  const training = keys.length ? sum(keys, (k) => months.get(k)?.training_hours ?? 0) : 0;
  return {
    ...dashboardStatistics(orgId, key),
    persona,
    organization_id: orgId,
    month_date: key ? `${key}-01` : null,
    branches,
    unassigned_waste: null,
    training_hours_total: branches.length ? round(training, 1) : null,
    previous_month_water_consumption: round(sum(prevRows, (r) => r.u.water_consumption), 1),
    previous_month_water_amount_sar: round(sum(prevRows, (r) => r.u.water_amount_sar), 2),
    previous_month_electricity_consumption_kwh: round(sum(prevRows, (r) => r.u.electricity_consumption_kwh), 0),
    previous_month_electricity_amount_sar: round(sum(prevRows, (r) => r.u.electricity_amount_sar), 2),
    previous_month_oil_consumption_liters: round(sum(prevRows, (r) => r.u.oil_consumption_liters), 1),
    previous_month_oil_amount_sar: round(sum(prevRows, (r) => r.u.oil_amount_sar), 2),
    previous_month_gas_consumption_m3: round(sum(prevRows, (r) => r.u.gas_consumption_m3), 1),
    previous_month_gas_amount_sar: round(sum(prevRows, (r) => r.u.gas_amount_sar), 2),
    previous_month_fill_level_report_count: sum(prevRows, (r) => r.fill),
    previous_month_training_hours_total: branches.length ? round(months.get(prevKey)?.training_hours ?? 0, 1) : null,
  };
}

/** Full-history export: lifetime per branch, one row per branch-month, lifetime per waste type. */
export function esgBranchWasteExport(orgId: string): EsgBranchWasteExportResponse {
  const branches = branchesFor(orgId);
  const keys = lastMonthKeys(12);
  const all = ordersInMonth(orgId, null);
  const branch_summary = branches.map((b) => {
    const mine = all.filter((o) => branchOf(orgId, o) === b.id);
    const requested = sum(mine, orderKg);
    const collected = sum(mine.filter((o) => o.status === 'completed'), orderKg);
    return {
      branch: b.name,
      total_waste_kg: round(collected, 1),
      total_recycled_kg: round(sum(recycledOrders(mine), (r) => r.recycledKg), 1),
      diversion_rate_percent: pct(collected, requested),
    };
  });
  const monthly_detail = keys.flatMap((key) => {
    const [year, month] = key.split('-').map(Number);
    return branches.map((b) => {
      const mine = ordersInMonth(orgId, key).filter((o) => branchOf(orgId, o) === b.id);
      const requested = sum(mine, orderKg);
      const collected = sum(mine.filter((o) => o.status === 'completed'), orderKg);
      const breakdown = materialBreakdown(mine)
        .map((row) => `${categoryName(row.waste_type)}: ${Math.round(row.total_kg).toLocaleString('en-US')} kg`)
        .join('; ');
      return {
        year,
        month,
        branch: b.name,
        waste_diverted_kg: round(collected, 1),
        waste_recycled_kg: round(sum(recycledOrders(mine), (r) => r.recycledKg), 1),
        diversion_rate_percent: pct(collected, requested),
        waste_breakdown: breakdown || null,
      };
    });
  });
  const waste_type_summary = materialBreakdown(all).map((row) => ({
    waste_type: categoryName(row.waste_type),
    total_waste_kg: row.total_kg,
  }));
  return { branch_summary, monthly_detail, waste_type_summary };
}
